import { footerLinks, socialLinks } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const sectionKeyMap: Record<string, string> = {
  product: 'footerLinks.product',
  developers: 'footerLinks.developers',
  company: 'footerLinks.company',
  legal: 'footerLinks.legal',
};

function FooterLink({ href, label, className }: { href: string; label: string; className: string }) {
  const isInternal = href.startsWith('/') && !href.startsWith('//');
  if (isInternal) {
    return (
      <Link to={href} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {label}
    </a>
  );
}

export default function Footer() {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#666]' : 'text-[#888]';
  const textMuted = isDark ? 'text-[#444]' : 'text-[#bbb]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bgWhite5 = isDark ? 'bg-white/5' : 'bg-black/5';
  const hoverTextWhite = isDark ? 'hover:text-white' : 'hover:text-[#111]';
  const hoverBgWhite5 = isDark ? 'hover:bg-white/5' : 'hover:bg-black/5';

  const footerSectionKeys = Object.keys(footerLinks) as Array<keyof typeof footerLinks>;

  return (
    <footer className={`relative w-full border-t ${borderLight} ${isDark ? 'bg-[#050505]' : 'bg-[#f5f5f5]'}`}>
      <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-[#00ff88]/20">
              <img
                src="https://readdy.ai/api/search-image?query=A%20futuristic%20AI-themed%20crypto%20exchange%20logo%20featuring%20the%20letters%20A%20and%20I%20fused%20into%20a%20single%20elegant%20monogram%20with%20electric%20neon%20green%20gradient%20glow%20and%20neural%20network%20node%20connections%2C%20minimal%20geometric%20design%20on%20a%20deep%20black%20background%20with%20subtle%20digital%20grid%20texture%2C%20professional%20brand%20identity%2C%20high%20resolution%2C%20clean%20sharp%20edges%2C%20no%20additional%20text%20or%20elements%2C%20centered%20composition%2C%20perfect%20for%20dark%20themed%20trading%20platform%20website%20header%20and%20app%20icon&width=256&height=256&seq=logo_zhisu_ai_v1&orientation=squarish"
                alt="ZhiSu"
                className="w-full h-full object-cover"
              />
            </div>
            <span className={`font-semibold text-lg tracking-tight ${textPrimary}`}>ZhiSu</span>
          </Link>
            <p className={`text-sm leading-relaxed max-w-xs ${textSecondary}`}>
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-3 mt-5">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${bgWhite5} ${borderLight} ${textSecondary} hover:text-[#00ff88] hover:border-[#00ff88]/30 hover:bg-[#00ff88]/5`}
                  aria-label={link.label}
                >
                  <i className={`${link.icon} w-4 h-4 flex items-center justify-center`} />
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${textPrimary}`}>
              {t('footer.product')}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.product.map((link, i) => (
                <li key={link.label}>
                  <FooterLink
                    href={link.href}
                    label={t(`content.footerLinks.product.${i}`, link.label)}
                    className={`text-sm transition-colors ${textSecondary} ${hoverTextWhite}`}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Developers */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${textPrimary}`}>
              {t('footer.developers')}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.developers.map((link, i) => (
                <li key={link.label}>
                  <FooterLink
                    href={link.href}
                    label={t(`content.footerLinks.developers.${i}`, link.label)}
                    className={`text-sm transition-colors ${textSecondary} ${hoverTextWhite}`}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${textPrimary}`}>
              {t('footer.company')}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.company.map((link, i) => (
                <li key={link.label}>
                  <FooterLink
                    href={link.href}
                    label={t(`content.footerLinks.company.${i}`, link.label)}
                    className={`text-sm transition-colors ${textSecondary} ${hoverTextWhite}`}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-4 ${textPrimary}`}>
              {t('footer.legal')}
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.legal.map((link, i) => (
                <li key={link.label}>
                  <FooterLink
                    href={link.href}
                    label={t(`content.footerLinks.legal.${i}`, link.label)}
                    className={`text-sm transition-colors ${textSecondary} ${hoverTextWhite}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`mt-12 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4 ${borderLight}`}>
          <p className={`text-xs ${textMuted}`}>
            &copy; 2026 ZhiSu. {t('footer.allRights')}
          </p>
          <div className={`flex items-center gap-2 text-xs font-mono ${textMuted}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
            <span>{t('footer.systemsOperational')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}